/* eslint-disable no-underscore-dangle --
 * `_hsq` is HubSpot's own global command queue name; it is not ours to rename.
 * The `__reset*ForTests` export follows the same convention as b2b-api's
 * `__resetRateLimiterForTests`.
 */

/**
 * HubSpot tracking pixel.
 *
 * Shaped to mirror ./posthog: env-derived enable flag, an `applyConsentTo*`
 * translator, and no import from ./consent — so consent.ts keeps its one-way
 * dependency on the SDK wrappers rather than the reverse.
 *
 * ── Two constraints worth knowing before editing this file ──
 *
 * 1. TIER. The portal this points at is Marketing Hub *Starter*.
 *    `trackCustomBehavioralEvent` requires Marketing Hub *Enterprise*: on
 *    Starter the call is accepted by `_hsq` and then silently discarded
 *    server-side. That is why there is no custom-event export here — a wrapper
 *    that no-ops invisibly is worse than no wrapper, because callers trust it.
 *    The only verbs that work on this tier are the four below.
 *
 * 2. COST. `identify()` followed by a pageview *creates* a contact if none
 *    exists, and the account has a hard, irreversible marketing-contact billing
 *    cliff. Nothing in this module calls `identify` on its own; the single
 *    caller (AnalyticsBootstrap) gates it on the contact already existing. See
 *    the comment on `hubspotIdentify`.
 */

const PORTAL_ID = import.meta.env.VITE_PUBLIC_HUBSPOT_PORTAL_ID as
  | string
  | undefined;

export const isHubSpotEnabled: boolean = Boolean(PORTAL_ID);

/**
 * Server-side kill switch, served with the cookie-banner config.
 *
 * Defaults to FALSE and only opens once b2b-api says so, which makes it the
 * primary gate rather than a backstop. Two reasons it has to work that way:
 *
 * - CI builds ONE artifact and deploys it to staging and production both
 *   (.github/workflows/main.yml), so `VITE_PUBLIC_HUBSPOT_PORTAL_ID` is baked
 *   identically into both. The env var can only decide whether the pixel is
 *   compiled in at all; it cannot distinguish environments. The server flag
 *   comes from each environment's own database, so it can.
 * - The pixel creates billable CRM contacts. Turning it off should not require a
 *   rebuild and redeploy.
 *
 * Failing closed also means a b2b-api outage silently disables the pixel rather
 * than defaulting it on.
 */
let allowedByServer = false;

export const setHubSpotPixelAllowed = (allowed: boolean): void => {
  allowedByServer = allowed;
};

/** Test hook: module state outlives `vi.resetModules()` within a file. */
export const __resetHubSpotPixelAllowedForTests = (): void => {
  allowedByServer = false;
};

const isPixelPermitted = (): boolean => isHubSpotEnabled && allowedByServer;

/** HubSpot's own id for the loader tag; reused so we can find and remove it. */
const SCRIPT_ID = 'hs-script-loader';

/**
 * Cookies HubSpot sets once the script runs. `hubspotutk` is the visitor token
 * that ties web activity to a contact; the `__hs*` trio are analytics session
 * state. Expired together on withdrawal.
 */
const HUBSPOT_COOKIES = ['hubspotutk', '__hstc', '__hssc', '__hssrc'];

type HsqCommand = unknown[];

declare global {
  interface Window {
    _hsq?: HsqCommand[];
  }
}

/**
 * `_hsq` is ours until the script loads, then HubSpot replaces `push` with its
 * real dispatcher and replays whatever we queued. Creating it eagerly is what
 * makes pre-load commands (notably `doNotTrack`) survive.
 */
const queue = (): HsqCommand[] => {
  window._hsq = window._hsq ?? [];
  return window._hsq;
};

const push = (command: HsqCommand): void => {
  try {
    queue().push(command);
  } catch {
    // Analytics must never be the reason a page breaks.
  }
};

export const isHubSpotLoaded = (): boolean => {
  try {
    return document.getElementById(SCRIPT_ID) !== null;
  } catch {
    return false;
  }
};

/**
 * Idempotent: React StrictMode double-invokes effects in dev, and
 * `applyConsent` runs both at boot and on every banner interaction.
 */
export const loadHubSpot = (): void => {
  if (!isPixelPermitted() || typeof document === 'undefined') return;
  if (isHubSpotLoaded()) return;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = `https://js.hs-scripts.com/${PORTAL_ID as string}.js`;
  script.async = true;
  script.defer = true;
  script.type = 'text/javascript';
  document.body.appendChild(script);
};

const expireCookie = (name: string): void => {
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  // HubSpot sets on the registrable domain, so clearing only the exact host
  // leaves the cookie alive on every sibling subdomain. Both are attempted;
  // a mismatched domain attribute is a no-op rather than an error.
  const domains = [
    '',
    `; domain=${window.location.hostname}`,
    `; domain=.${window.location.hostname.split('.').slice(-2).join('.')}`
  ];
  domains.forEach((domain) => {
    document.cookie = `${name}=; expires=${past}; path=/${domain}`;
  });
};

/**
 * Removes the loader tag and expires HubSpot's cookies.
 *
 * Honest about its limits: removing a `<script>` node does not unload JavaScript
 * that has already executed, so a listener HubSpot installed in this page load
 * may still be live until the next navigation. The cookie expiry is the part
 * that carries the legal weight, and it takes effect immediately.
 */
export const unloadHubSpot = (): void => {
  if (typeof document === 'undefined') return;

  try {
    document.getElementById(SCRIPT_ID)?.remove();
    document
      .querySelectorAll(
        'script[src*="hs-analytics"], script[src*="hs-banner"], script[src*="hsadspixel"], script[src*="usemessages"]'
      )
      .forEach((node) => node.remove());
    HUBSPOT_COOKIES.forEach(expireCookie);
    // `_hsq` is deliberately LEFT IN PLACE. It holds the `doNotTrack` command
    // that the withdrawal path pushes immediately before calling this, and
    // deleting it would discard the very instruction that keeps a later script
    // load from setting a cookie. It is an empty-ish array of strings, so
    // keeping it costs nothing.
  } catch {
    // Best effort.
  }
};

/** Current path including query, which is what HubSpot records as the page. */
const currentPath = (): string =>
  `${window.location.pathname}${window.location.search}`;

export const hubspotSetPath = (path: string): void => push(['setPath', path]);

export const hubspotTrackPageView = (): void => push(['trackPageView']);

/**
 * Associates the current browser (via `hubspotutk`) with a contact.
 *
 * `identify` alone transmits nothing — HubSpot only flushes identities on the
 * next `trackPageView`, which is why the pair is always sent together here.
 *
 * DO NOT call this for an arbitrary logged-in user. HubSpot has no
 * "link only if the contact already exists" API: identify + pageview is an
 * upsert, so an unconditional call turns every portal login into a new HubSpot
 * contact and walks the account toward its marketing-contact billing cliff.
 * The caller in HubSpotGate.tsx asks b2b-api whether the contact exists first;
 * that check is the cost control, not an optimisation.
 *
 * The address MUST come from the WorkOS session. Never pass one a user typed
 * into a form: the portal has inputs for inviting colleagues, and binding this
 * browser to an invitee would attribute the inviter's whole session to them.
 */
export const hubspotIdentify = (email: string): void => {
  if (!isHubSpotLoaded()) return;
  push(['identify', { email }]);
  hubspotTrackPageView();
};

/**
 * Forgets whoever this browser was last associated with, without withdrawing
 * consent or unloading the pixel.
 *
 * Called on logout. HubSpot has no "un-identify" command — the association
 * lives in the `hubspotutk` cookie — so expiring the visitor token is the only
 * way to end it. Without this, the next person to use a shared machine browses
 * as the previous user until they sign in and re-identify, and everything in
 * between is filed against the wrong contact. The pixel mints a fresh
 * anonymous token on the next pageview.
 */
export const hubspotResetIdentity = (): void => {
  if (typeof document === 'undefined') return;

  try {
    HUBSPOT_COOKIES.forEach(expireCookie);
  } catch {
    // Best effort; analytics must never be the reason logout fails.
  }
};

/**
 * Translate a consent decision into pixel state.
 *
 * Deliberately ASYMMETRIC with `applyConsentToPostHog`, and the middle tier is
 * where they diverge:
 *
 * - analytics off          → never inject; `doNotTrack`; tear down.
 * - analytics on, sale off → ALSO never inject. PostHog has a memory-persistence
 *                            mode that captures events without durable storage;
 *                            HubSpot has no equivalent, because the pixel *is*
 *                            the `hubspotutk` cookie. There is no anonymous
 *                            mode to map this tier onto, so the honest
 *                            behaviour is to stay off rather than to load
 *                            something that cannot honour the choice.
 * - analytics on, sale on  → inject, then record the entry pageview.
 *
 * `doNotTrack` is pushed BEFORE the tag would be appended: the queue is replayed
 * on load, so this is the only ordering in which the script can boot without
 * setting a cookie first.
 */
export const applyConsentToHubSpot = (categories: {
  analytics: boolean;
  sale: boolean;
}): void => {
  if (!isHubSpotEnabled) return;

  // Not folded into the branch below: when the server switch is off we still
  // want the teardown path to run, so that flipping it off mid-session removes
  // an already-injected pixel and its cookies rather than merely freezing it.
  if (!allowedByServer || !categories.analytics || !categories.sale) {
    push(['doNotTrack']);
    unloadHubSpot();
    return;
  }

  const alreadyLoaded = isHubSpotLoaded();
  loadHubSpot();

  // Only on the transition into consent. The script fires its own pageview on
  // load, but before `setPath` has been applied — so this pair is what makes the
  // entry view land on the right path. Repeating it for an already-loaded pixel
  // would double-count the entry.
  if (!alreadyLoaded) {
    hubspotSetPath(currentPath());
    hubspotTrackPageView();
  }
};
